import React from 'react'
import Button from 'components/Button'
import FormSubmitButton from 'components/FormSubmitButton'
import TextArea from 'components/TextArea'
import TextField from 'components/TextField'
import { Grid, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { AddCircle, Visibility } from '@mui/icons-material'
import GridExpandable from 'components/GridExpandable'
import { FormProvider } from 'react-hook-form'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import ReferenceFormPreview from './components/ReferenceFormPreview'
import { IAddReferenceFormValues, useAddReferenceForm } from './useAddReferenceForm'

export interface IAddReferenceFormProps {
  submitReference: (values: IAddReferenceFormValues) => void
  loading: boolean
  profileName: string
}

function AddReferenceForm(props: IAddReferenceFormProps) {
  const { loading, profileName, submitReference } = props
  const theme = useTheme()
  const form = useAddReferenceForm({}, submitReference)
  const { useFormField, getValues, submit } = form
  const [previewExpanded, setPreviewExpanded] = React.useState(false)
  const isSmallScreen = useResponsiveBreakpoint(theme.breakpoints.values.md)
  const firstName = getValues().firstName
  const lastName = getValues().lastName
  const name = `${firstName || ''} ${lastName || ''}`.trim()

  // auto-close preview when name field is cleared
  React.useEffect(() => {
    if (previewExpanded && !firstName && !lastName) {
      setPreviewExpanded(false)
    }
  }, [firstName, lastName, previewExpanded])

  // TODO: using the form onSubmit handler seems to fail to invoke the submit handler
  // it's probably because this is a form within another form (the main profile form)
  // for now, the workaround is submitting the form from the submit button's click handler instead
  return (
    <FormProvider {...form}>
      <form>
        <Grid container spacing={2}>

          <Grid item xs={12} sm={3}>
            <TextField {...useFormField('firstName', 'typing')} label="First Name" fullWidth disabled={loading} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField {...useFormField('lastName', 'typing')} label="Last Name" fullWidth disabled={loading} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField {...useFormField('email', 'typing')} label="Email" fullWidth disabled={loading} />
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormSubmitButton
              onClick={submit}
              color="secondary"
              dataCy="send-reference"
              icon={<AddCircle />}
              label="Send"
            />
          </Grid>

          <GridExpandable item xs={12} style={{ textAlign: 'right' }} expand={Boolean(name) && !isSmallScreen}>
            <Button onClick={() => setPreviewExpanded(!previewExpanded)} startIcon={<Visibility />}>
              See what they will receive
            </Button>
          </GridExpandable>

          <GridExpandable item xs={12} expand={previewExpanded && !isSmallScreen}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption">
                  They will receive this message
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextArea
                  disabled
                  label="Message"
                  value={[
                    `Hi, ${firstName},`,
                    '',
                    `${profileName} has created a profile on Flexhire - a global platform for finding roles with top companies. They have requested a quick favor from you. Would you be willing to write a super brief reference via this [link] for ${profileName}? They'd like to add it to their profile on flexhire.com to help their career. It should take you less then a minute.`,
                    '',
                    `Thanks so much for helping ${profileName}!`,
                    'The Flexhire team',
                  ].join('\n')}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption">
                  And a link to this form
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <ReferenceFormPreview name={profileName} />
              </Grid>
            </Grid>
          </GridExpandable>
        </Grid>
      </form>
    </FormProvider>
  )
}

export default React.memo(AddReferenceForm)
